// @architect-approved: INFRASTRUCTURE - SequenceRouteLoader.tsx is a routing loader, not a DB-registered component; does not follow component identity pattern
/**
 * SequenceRouteLoader.tsx
 *
 * Route component for /component/:seq pattern
 * Uses existing createComponentBySeq() utility from componentLoader.ts
 *
 * Created: 2026-06-06
 */

import { Box, CircularProgress, Typography } from '@mui/material';
import { Suspense, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { createComponentBySeq } from './componentLoader';
import { getContainerMaxWidthPx } from './containerWidthStrategy';
import sequenceManifest from '../generated-component-seq-map.json';

// Loading fallback
const LoadingFallback = () => (
    <Box
        sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
        }}
    >
        <CircularProgress />
    </Box>
);

// Error state
interface ErrorStateProps {
    seq: string;
    message: string;
}

const ErrorState = ({ seq, message }: ErrorStateProps) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            gap: 2,
            p: 3,
        }}
    >
        <Typography variant="h5" color="error">
            Component Not Found
        </Typography>
        <Typography
            variant="body1"
            sx={{
                color: 'text.secondary',
            }}
        >
            Sequence: {seq}
        </Typography>
        <Typography
            variant="body2"
            sx={{
                color: 'text.secondary',
            }}
        >
            {message}
        </Typography>
    </Box>
);

/**
 * SequenceRouteLoader - Loads component by sequence number from URL
 */
export function SequenceRouteLoader() {
    const { seq } = useParams<{ seq: string }>();

    // Validate sequence parameter
    const sequenceNum = seq ? parseInt(seq, 10) : NaN;

    // Create lazy component using existing utility (hooks must be at top level)
    const LazyComponent = useMemo(
        () => (seq && !isNaN(sequenceNum) ? createComponentBySeq(sequenceNum) : null),
        [seq, sequenceNum],
    );

    // Key the rendered element on seq so navigating between two /component/:seq
    // deep links always produces a genuine unmount+remount instead of an
    // in-place update.
    const mountKey = seq;

    if (!seq || isNaN(sequenceNum)) {
        return <ErrorState seq={seq || 'unknown'} message="Invalid sequence number" />;
    }

    // Check if component exists in manifest
    const componentMetadata = (sequenceManifest.sequenceMap as any)[sequenceNum];

    if (!componentMetadata || !LazyComponent) {
        return <ErrorState seq={seq} message={`Sequence ${seq} not found in component registry`} />;
    }

    // Determine if component is a directory (nav_, dirm_, gate_ prefixes)
    const isDirectory =
        componentMetadata.prefix &&
        ['nav', 'dirm', 'dirs', 'gate'].includes(componentMetadata.prefix);

    // fixmod_ = hand-built, solution-specific FIXED layout shells (Fixed/Hug/Fill
    // panel composition, e.g. fixmod_tgc_11860_ideation_hub). These manage their
    // OWN internal scrolling per-panel (see PanelSlot), so the route outlet must
    // give them a hard, non-scrolling 100% height instead of the generic
    // "auto-grow + page scroll" Picture Frame used by scr_/mod_ CRUD screens.
    // See: repo memory sequence-route-loader-picture-frame-overflow-clip-bug.md
    // (that fix is intentionally kept for everything EXCEPT fixmod_).
    const isFixedShell = componentMetadata.prefix === 'fixmod';

    // Optional per-component max-width, set via Component Registry "Set TSX Width" context menu
    const maxWidthPx = getContainerMaxWidthPx(componentMetadata.containerWidthStrategyId);

    // Render component (no per-route security wrapper — AuthGuard at the
    // App.tsx root already gates every route, including this one; a second,
    // independent token-check wrapper here was redundant and stateful in a
    // way that caused it to fight with AuthGuard. Removed 2026-09-29,
    // matching the same fix already applied to RouteLoaderEncoded.tsx.
    // PICTURE FRAME PATTERN: Provide flex container for direct-routed components
    // Same pattern as ComponentViewer - host provides sized container, child fills space
    return isDirectory ? (
        <Suspense fallback={<LoadingFallback />}>
            <LazyComponent key={mountKey} />
        </Suspense>
    ) : (
        <Suspense fallback={<LoadingFallback />}>
            {isFixedShell ? (
                /* FIXED SHELL FRAME (fixmod_): hard 100% height, no page-level
                       scroll — the module owns its own internal layout/scrolling
                       per panel. Do NOT use minHeight/overflow:auto here, that
                       collapses the percentage-height chain back to 'auto' and
                       lets content (e.g. panel stub placeholders) balloon past
                       the viewport instead of being constrained. */
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        width: '100%',
                        maxWidth: maxWidthPx ?? 'none',
                        mx: maxWidthPx ? 'auto' : 0,
                        overflow: 'hidden',
                    }}
                >
                    <LazyComponent key={mountKey} />
                </Box>
            ) : (
                /* Picture Frame: Flex container that fills App.tsx outlet (flex: 1).
                        overflow: 'auto' (not 'hidden') - fills available height like before,
                        but shows a scrollbar instead of silently clipping content that grows
                        taller than the viewport (e.g. a grid + form below it after adding rows).
                        See: repo memory sequence-route-loader-picture-frame-overflow-clip-bug.md */
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                        width: '100%',
                        overflow: 'auto',
                    }}
                >
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: maxWidthPx ?? 'none',
                            mx: maxWidthPx ? 'auto' : 0,
                            minHeight: '100%',
                        }}
                    >
                        <LazyComponent key={mountKey} />
                    </Box>
                </Box>
            )}
        </Suspense>
    );
}

export default SequenceRouteLoader;
